<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { library } from '@fortawesome/fontawesome-svg-core'
import { faCheck, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons'

import {
  PedalKnob,
  CcBadge,
  BounceForm,
  FlipflopForm,
  GlitchForm,
  SwellForm,
  WanderForm,
  ZigzagForm,
  effects,
  isCcMappingSupported,
  rateDivisionOptions as pickRateDivisionOptions,
  accentEveryOptions,
  isAccentEverySupported,
  isAccentEveryVisible,
  accentEveryValue,
  setAccentEvery,
  accentEveryTip,
  PHASE_OFFSET_RANGE,
  PHASE_OFFSET_SNAPS,
  isPhaseOffsetVisible,
  phaseOffsetValue,
  setPhaseOffset,
  phaseOffsetTip,
} from '@midulate/shared';

import motifBounce   from '~/assets/images/motifs/Bounce.png';
import motifFlipFlop from '~/assets/images/motifs/FlipFlop.png';
import motifGlitch   from '~/assets/images/motifs/Glitch.png';
import motifSwell    from '~/assets/images/motifs/Swell.png';
import motifWander   from '~/assets/images/motifs/Wander.png';
import motifZigZag   from '~/assets/images/motifs/ZigZag.png';

const { EFFECT_TITLES, ACCENT_EFFECT_TYPES, retypeEffect } = effects;

library.add(faCheck, faPenToSquare, faTrash);

const motifPngs = {
  bounce:   motifBounce,
  flipflop: motifFlipFlop,
  glitch:   motifGlitch,
  swell:    motifSwell,
  wander:   motifWander,
  zigzag:   motifZigZag,
};

const capitalize = ([first, ...rest]) => first.toUpperCase() + rest.join('');

// Ordered clock-face: None=12, Small+=2, Large+=4, Mask=6, Large-=8, Small-=10
const ACCENT_OPTIONS = [
  { value: 'none',           label: 'None'    },
  { value: 'small_positive', label: 'Small +' },
  { value: 'large_positive', label: 'Large +' },
  { value: 'mask',           label: 'Mask'    },
  { value: 'large_negative', label: 'Large −' },
  { value: 'small_negative', label: 'Small −' },
];

export default {
  components: {
    FontAwesomeIcon,
    PedalKnob,
    CcBadge,
    BounceForm,
    FlipflopForm,
    GlitchForm,
    SwellForm,
    WanderForm,
    ZigzagForm
  },

  inject: {
    midiMonitor: { default: () => ({ key: null }) },
    capabilities: { default: () => ({ capabilities: {} }) },
    ccOverlay: { default: () => ({ active: false }) },
  },

  data() {
    return {
      effectTitles: EFFECT_TITLES,
      editingChannelName: false,
      editingCCName: false,
      accentOptions: ACCENT_OPTIONS,
      PHASE_OFFSET_RANGE,
      PHASE_OFFSET_SNAPS,
    }
  },

  computed: {
    active: function() {
      if (!this.connectedToDevice) return true;
      if (this.solo) return true;
      return !this.mute;
    },

    accentCapable() {
      return ACCENT_EFFECT_TYPES.includes(this.effect?.type);
    },

    // Div knob options: note-value durations when the device advertises
    // `note_divisions` (MDL-288), otherwise the legacy bar-fraction list.
    rateDivisionOptions() {
      return pickRateDivisionOptions(this.capabilities);
    },

    noteDivisionsCapable() {
      return !!(this.capabilities?.capabilities?.note_divisions);
    },

    // Full-scale tooltip. In note-value mode, list every step (British ·
    // American) so the user can see where they are in the sweep (spec §5).
    rateDivisionTip() {
      if (!this.noteDivisionsCapable) {
        return 'Bar division for the effect cycle length.';
      }
      return 'Cycle length as a note value.';
    },

    // Accent Every (MDL-297): shown beside Accent once an accent is set, on
    // firmware that advertises `accent_every`.
    accentEveryVisible() {
      return isAccentEveryVisible(this.effect, this.capabilities);
    },

    accentEveryCurrent() {
      return accentEveryValue(this.effect);
    },

    accentEveryChoices() {
      return accentEveryOptions(this.accentEveryCurrent);
    },

    accentEveryHint() {
      return accentEveryTip(this.accentEveryCurrent, this.effect.rate, this.meter);
    },

    // Phase Offset (MDL-301): one knob in Hz and sync mode, on firmware that
    // advertises `phase_offset`.
    phaseVisible() {
      return isPhaseOffsetVisible(this.effect, this.capabilities);
    },

    phaseCurrent() {
      return phaseOffsetValue(this.effect);
    },

    phaseHint() {
      return phaseOffsetTip(this.effect);
    },

    accentTip() {
      const base = 'Adjusts the peak on the first beat of each bar. Only noticeable when the cycle is one bar or shorter.';
      return isAccentEverySupported(this.capabilities)
        ? `${base} Use Every to accent every Nth cycle instead.`
        : base;
    },

    ccOverlayVisible() {
      return this.ccOverlay.active && isCcMappingSupported(this.capabilities);
    },

    motifPng() {
      return motifPngs[this.effect?.type] ?? null;
    },

    midiKey() {
      if (this.effect?.channel == null || this.effect?.cc == null) return null;
      return `${this.effect.channel}:${this.effect.cc}`;
    },

    isHighlighted() {
      return this.midiKey !== null && this.midiMonitor.key === this.midiKey;
    },
  },

  props: {
    effectIndex: {
      type: Number,
      required: true
    },

    patchIndex: {
      type: Number,
      required: true
    },

    effect: {
      type: Object,
      required: true
    },

    devices: {
      type: Array,
      required: true
    },

    solo: {
      type: Boolean,
      required: false,
      default: false
    },

    mute: {
      type: Boolean,
      required: false,
      default: false
    },

    connectedToDevice: {
      type: Boolean,
      required: true,
      default: false
    },

    // Beats per bar of the patch, for the Accent Every captions.
    meter: {
      type: Number,
      required: false,
      default: 4
    },

    dragging: {
      type: Boolean,
      required: false,
      default: false
    },

    dragHandleListeners: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  methods: {
    setAccentEvery,
    setPhaseOffset,

    channel_names() {
      return Array.from({ length: 16 }, (_, i) => {
        if (this.$props.devices && this.$props.devices[i]) {
          return `${i + 1} - ${this.$props.devices[i].name}`;
        } else {
          return `${i + 1}`;
        }
      });
    },

    ccLabel(channel, cc) {
      if (!this.$props.devices) return;
      if (!this.$props.devices[channel - 1]) return;
      if (!this.$props.devices[channel - 1].cc) return;
      if (!this.$props.devices[channel - 1].cc[cc]) return;

      return ' - ' + this.$props.devices[channel - 1].cc[cc];
    },

    ccLabels(channel) {
      if (!this.$props.devices) return;
      if (!this.$props.devices[channel - 1]) return;
      if (!this.$props.devices[channel - 1].cc) return;

      const labels = Array.from({ length: 128 }, (_, i) => {
        return this.$props.devices[channel - 1].cc[i] || null;
      });
      return labels;
    },

    // A bare v-model here would swap the type and leave the params alone —
    // the old type's as dead keys, the new type's absent — so the knobs would
    // read 0 while the device used its own defaults. Rebuild the params
    // instead, keeping what belongs to the effect rather than its type.
    changeType(type) {
      if (type === this.effect.type) return;
      retypeEffect(this.effect, type);
    },

    deleteEffect() {
      this.$emit('deleteEffect', this.patchIndex, this.effectIndex);
    },

    editCCName() {
      const { devices, effect } = this.$props;
      const device = devices[effect.channel - 1];

      if (!device) return;

      if (!device.cc) {
        device.cc = { [effect.cc]: '' };
      }

      this.editingCCName = true;
    },

    effectTypeForm() {
      if (!this.effect || !this.effect.type) return null;
      return `${capitalize(this.effect.type)}Form`;
    },

    showDeleteModal() {
      const modal = document.getElementById(`my_modal_${this.patchIndex}_${this.effectIndex}`);
      if (modal) {
        modal.showModal();
      }
    },

    onClockSyncChange(enabled) {
      if (enabled) {
        // Switching to clock-sync: set a valid bar-division string so the
        // enum PedalKnob gets a proper match (avoids the number/string mismatch).
        this.effect.clock_sync = true;
        if (typeof this.effect.rate !== 'string') {
          this.effect.rate = '1';
        }
      } else {
        // Switching to free Hz: set a numeric rate so the number input is valid.
        this.effect.clock_sync = false;
        if (typeof this.effect.rate !== 'number') {
          this.effect.rate = 0.25;
        }
      }
    }
  },

  watch: {
    effect: {
      handler(newEffect) {
        if (newEffect.channel == null || newEffect.cc == null) return;
        this.$emit('updateEffect', {
          patchIndex: this.patchIndex,
          effectIndex: this.effectIndex,
          newEffect: newEffect
        });
      },
      deep: true
    }
  }
}
</script>
<template>
  <!-- Pedal shell -->
  <div
    :class="['pedal', `pedal--${effect.type}`, active ? 'pedal--active' : 'pedal--inactive', isHighlighted ? 'pedal--midi-highlight' : '', dragging ? 'pedal--dragging' : '', ccOverlayVisible ? 'pedal--cc-overlay' : '']"
    @mouseenter="midiMonitor.key = midiKey"
    @mouseleave="midiMonitor.key = null"
  >

    <!-- ── TOP PLATE ── -->
    <div
      class="pedal__top"
      draggable="true"
      v-on="dragHandleListeners"
    >
      <span class="pedal__name">{{ effectTitles[effect.type] }}</span>
      <img v-if="motifPng" class="pedal__motif" :src="motifPng" :alt="effectTitles[effect.type]" />
    </div>

    <!-- ── BODY ── -->
    <div class="pedal__body">

      <!-- Type select -->
      <div class="pedal__type-select">
        <span class="pedal__type-label">Type</span>
        <select :value="effect.type" @change="changeType($event.target.value)">
          <option value="bounce">{{ effectTitles.bounce }}</option>
          <option value="flipflop">{{ effectTitles.flipflop }}</option>
          <option value="glitch">{{ effectTitles.glitch }}</option>
          <option value="swell">{{ effectTitles.swell }}</option>
          <option value="wander">{{ effectTitles.wander }}</option>
          <option value="zigzag">{{ effectTitles.zigzag }}</option>
        </select>
      </div>

      <!-- Clock Sync row — label matches Type label width, toggle matches Type select -->
      <div class="pedal__rate">
        <span class="pedal__rate-label tooltip tooltip-left" data-tip="Lock the modulation cycle to the MIDI clock as a bar division. When off, set speed freely in Hz.">Sync</span>
        <label class="pedal__clock-sync-toggle">
          <input
            type="checkbox"
            class="toggle toggle-xs"
            :checked="effect.clock_sync"
            @change="onClockSyncChange($event.target.checked)"
          />
        </label>
      </div>

      <!-- Free-rate knob (log 0.1–10 Hz, shown when clock sync is off) -->
      <div v-if="!effect.clock_sync" class="pedal-knobs">
        <CcBadge :effect="effect" param="rate">
          <PedalKnob
            label="Rate"
            tip="Speed of the modulation cycle in Hz."
            :min="0.1"
            :max="10"
            :logarithmic="true"
            :value="typeof effect.rate === 'number' ? Math.min(10, Math.max(0.1, effect.rate)) : 0.25"
            @update:value="v => effect.rate = v"
          />
        </CcBadge>
        <CcBadge v-if="phaseVisible" :effect="effect" param="phase_offset">
          <PedalKnob
            label="Phase"
            :tip="phaseHint"
            mode="bipolar"
            :range="PHASE_OFFSET_RANGE"
            :snaps="PHASE_OFFSET_SNAPS"
            unit="°"
            :value="phaseCurrent"
            @update:value="v => setPhaseOffset(effect, v)"
          />
        </CcBadge>
      </div>

       <!-- Div + Accent knobs (shown when clock sync is on, centred like main knobs) -->
       <div v-if="effect.clock_sync" class="pedal-knobs">
         <CcBadge :effect="effect" param="rate">
           <PedalKnob
             label="Div"
             :tip="rateDivisionTip"
             mode="enum"
             :options="rateDivisionOptions"
             :value="typeof effect.rate === 'string' ? effect.rate : '1'"
             @update:value="v => effect.rate = v"
           />
         </CcBadge>
         <CcBadge v-if="phaseVisible" :effect="effect" param="phase_offset">
           <PedalKnob
             label="Phase"
             :tip="phaseHint"
             mode="bipolar"
             :range="PHASE_OFFSET_RANGE"
             :snaps="PHASE_OFFSET_SNAPS"
             unit="°"
             :value="phaseCurrent"
             @update:value="v => setPhaseOffset(effect, v)"
           />
         </CcBadge>
         <div v-if="accentCapable" class="pedal__accent">
           <CcBadge :effect="effect" param="accent">
             <PedalKnob
               label="Accent"
               :tip="accentTip"
               mode="enum"
               variant="circular"
               :options="accentOptions"
               :value="effect.accent ?? 'none'"
               @update:value="v => effect.accent = v"
             />
           </CcBadge>
         </div>
         <CcBadge v-if="accentEveryVisible" :effect="effect" param="accent_every">
           <PedalKnob
             label="Every"
             :tip="accentEveryHint"
             mode="enum"
             :options="accentEveryChoices"
             :value="accentEveryCurrent"
             @update:value="v => setAccentEvery(effect, v)"
           />
         </CcBadge>
       </div>

      <!-- Effect-type knobs (each form renders .pedal-knobs rows) -->
      <component :is="effectTypeForm()" :effect="effect" />

      <!-- Divider strip -->
      <div class="pedal__strip"></div>

      <!-- Channel row -->
      <div class="pedal__routing">
        <div class="pedal__routing-row" v-if="editingChannelName">
          <span class="pedal__routing-label tooltip tooltip-left" data-tip="Rename the MIDI device for this channel.">Ch Name</span>
          <input
            type="text"
            class="pedal__routing-name-input"
            v-model="devices[effect.channel - 1].name"
            @blur="editingChannelName = false"
          />
          <button class="pedal__routing-edit" @click="editingChannelName = false">
            <FontAwesomeIcon :icon="['fas', 'check']" />
          </button>
        </div>
        <div class="pedal__routing-row" v-else>
          <span class="pedal__routing-label tooltip tooltip-left" data-tip="MIDI Channel for the effect.">Channel</span>
          <select
            v-model.number="effect.channel"
            class="pedal__routing-select"
          >
            <option v-for="(name, index) in channel_names()" :key="index" :value="index + 1">{{ name }}</option>
          </select>
          <button
            class="pedal__routing-edit"
            @click="editingChannelName = true"
            :disabled="effect.channel === undefined"
          >
            <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
          </button>
        </div>

        <!-- CC row -->
        <div class="pedal__routing-row" v-if="editingCCName">
          <span class="pedal__routing-label tooltip tooltip-left" data-tip="Rename the MIDI CC for this effect.">CC Name</span>
          <input
            type="text"
            class="pedal__routing-name-input"
            v-model="devices[effect.channel - 1].cc[effect.cc]"
            @blur="editingCCName = false"
          />
          <button class="pedal__routing-edit" @click="editingCCName = false">
            <FontAwesomeIcon :icon="['fas', 'check']" />
          </button>
        </div>
        <div class="pedal__routing-row" v-else>
          <span class="pedal__routing-label tooltip tooltip-left" data-tip="Control Change (CC) number for the effect.">CC</span>
          <select
            v-model.number="effect.cc"
            class="pedal__routing-select"
          >
            <option v-for="cc in 128" :key="cc" :value="cc">
              {{ cc }}{{ ccLabel(effect.channel, cc) }}
            </option>
          </select>
          <button
            class="pedal__routing-edit"
            @click="editCCName"
            :disabled="effect.cc === undefined"
          >
            <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
          </button>
        </div>
      </div>

    </div><!-- /pedal__body -->

    <!-- ── FOOT SECTION ── -->
    <div class="pedal__foot">

      <!-- Solo / Mute (live performance only) -->
      <div v-if="connectedToDevice" class="pedal__sm-btns">
        <button
          class="pedal__btn-solo"
          :class="{ 'pedal__btn-solo--active': solo }"
          @click="$emit('toggleSolo', effectIndex)"
        >S</button>
        <CcBadge :effect="effect" param="mute" marker="dot">
          <button
            class="pedal__btn-mute"
            :class="{ 'pedal__btn-mute--active': mute }"
            @click="$emit('toggleMute', effectIndex)"
          >M</button>
        </CcBadge>
      </div>

      <!-- Stomp / delete (bottom-right) -->
      <button class="pedal__stomp" @click="showDeleteModal()">
        <FontAwesomeIcon :icon="['fas', 'trash']" />
      </button>

      <!-- Delete confirmation dialog -->
      <dialog :id="`my_modal_${patchIndex}_${effectIndex}`" class="modal">
        <div class="modal-box">
          <h3 class="text-lg font-bold">Are you sure?</h3>
          <p class="py-4">This will delete the {{ effectTitles[effect.type] }} effect on channel {{ effect.channel }}, CC {{ effect.cc }}.</p>
          <div class="modal-action">
            <form method="dialog">
              <button class="btn btn-ghost">Cancel</button>
              <button class="btn btn-ghost text-error" @click="deleteEffect">
                <FontAwesomeIcon :icon="['fas', 'trash']" /> Yes, delete it
              </button>
            </form>
          </div>
        </div>
      </dialog>

    </div><!-- /pedal__foot -->

  </div><!-- /pedal -->
</template>
